import {
  Box, Button, ButtonGroup, Card, CardContent, CircularProgress, Grid, TextField,
  Typography, FormControl, Select, MenuItem, Checkbox, LinearProgress, Stack,
  IconButton, Tooltip,
} from '@mui/material'
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import { withStyles, ClassNameMap } from '@mui/styles'
import Autocomplete from '@mui/material/Autocomplete'
import React, { useEffect, useState } from 'react'
import { PatientService } from '../../../../services/patient.service'
import { checkCondition } from '../common/condition-check'
import Helper from '../../../../utils/helper'
import stylesheet from '../detail-container.stylesheet'
import ConfirmationDialogComponent from './confirmation-dialog.component'
import { toast } from 'react-toastify'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'

interface IProps {
  medicineInfo: any
  handleClose: any
  input: any
  outer: any
  outerSubmit: any
  differently: boolean
  heading: any
  noDropDown: any
  uiResource: any
  writeMode: boolean
  action?: string
  patientId: any
  currentStage: any
  classes: ClassNameMap
}

const MedicationsComponent: React.FC<IProps> = ({
  action,
  medicineInfo,
  classes,
  handleClose,
  input,
  outer,
  outerSubmit,
  differently,
  heading,
  noDropDown,
  uiResource,
  patientId,
  currentStage,
  writeMode,
}) => {
  const [oneMedicine, setOneMedicine] = useState()
  const [sigRating, setSigRating] = useState<boolean | null>(null)
  const options = input?.isMedReconcile
    ? ['taking', 'not taking', 'taking differently', 'document discontinue']
    : ['taking', 'not taking', 'taking differently']

  const [write, setWrite] = useState(writeMode)
  const [currentOption, setCurrentOption] = useState(checkCondition(action === 'edit', medicineInfo?.intake_regularity, 'taking'))
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [, setSaveDisable] = useState(medicineInfo ? false : true)
  const [confirmContent, setConfirmContent] = useState('');
  const { updatePatientData } = useUpdateDataAction(patientId)

  const initialValues = medicineInfo
    ? {
      medicineData: { id: medicineInfo?.id || '', name: medicineInfo?.name || '', med_id: medicineInfo?.med_id },
      dose: medicineInfo.c_schedule.dose || '',
      num_units: medicineInfo.pu_order?.num_units,
      unit: medicineInfo.pu_order?.unit,
      route: medicineInfo.pu_order?.route,
      frequency: medicineInfo.pu_order?.frequency,
      prescription_taken: medicineInfo.prescription_taken,
      schedule_name: medicineInfo?.c_schedule?.name,
      disallow_substitution: medicineInfo?.disallow_substitution,
      nn_sig_directions: medicineInfo?.nn_sig_directions || '',
      current_dose_start_date: medicineInfo?.current_dose_start_date || null
    }
    : {
      medicineData: { id: '', name: '', med_id: '' },
      dose: '',
      num_units: '',
      unit: '',
      route: '',
      frequency: '',
      prescription_taken: false,
      schedule_name: '',
      disallow_substitution: null,
      nn_sig_directions: '',
      current_dose_start_date: null
    }
  const [values, setValues] = useState(initialValues as any)
  const [FOOD_QUALIFIER, setFoodQualifier] = useState() as any
  const [MEDICATION_TIMINGS, setMedicineTiming] = useState() as any
  const [dosageInfo, setDosageInfo] = useState(medicineInfo?.dosage_info || [] as any)
  const [query, setQuery] = useState('')

  const onBtnChange = (_input: string) => {
    setCurrentOption(_input)
    if (action === 'edit') {
      if (_input === 'not taking') {
        setWrite(false)
      } else {
        setWrite(true)
      }
    }
  }

  const handleInputChange = (e: any) => {
    e.stopPropagation()
    e.preventDefault()

    if (e.target.name === 'current_dose_start_date') {
      const ts = e.target.value ? Math.floor(new Date(e.target.value + 'T12:00:00').getTime() / 1000) : null
      setValues({ ...values, current_dose_start_date: ts })
      return
    }

    const newValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    const newValues = { ...values, [e.target.name]: newValue }

    setValues(newValues)
    checkForm(e.target.name)

    if (e.target.name === 'frequency') {
      handleSigsChange(e) // Pass the updated values
    }
  }

  const handleSigsChange = (e: any) => {
    console.log('[handleSigsChange] called:', { name: e.target.name, value: e.target.value, num_units: values.num_units })
    let payload = {} as any
    const fq = e.target.name === 'frequency' ? e.target.value : values?.frequency || ''

    if (e.target.name === 'nn_sig_directions')
      payload = { nn_sig_directions: e.target.value }
    else if (values.num_units && fq)
      payload = {
        dose: values.dose,
        frequency: fq,
        unit: values.unit,
        route: values.route,
        num_units: values.num_units,
      }
    if (Object.keys(payload)?.length > 0) {
      setLoading(true)
      // Capture the new frequency value before async call to avoid closure issues
      const newFrequency = e.target.name === 'frequency' ? e.target.value : null
      patientService.getSigsConversion(payload).then((res: any) => {
        // Use functional form of setValues to get current state
        setValues((prevValues: any) => {
          if (res?.data?.result) {
            if (e.target.name === 'nn_sig_directions') {
              return {
                ...prevValues,
                frequency: res.data?.result?.frequency || prevValues.frequency,
                num_units: res.data?.result?.num_units || prevValues.num_units,
              }
            } else {
              return {
                ...prevValues,
                frequency: newFrequency !== null ? newFrequency : (res.data?.result?.frequency || prevValues.frequency),
                nn_sig_directions: res.data?.result?.nn_sig_directions || prevValues.nn_sig_directions,
              }
            }
          } else {
            // API returned no result - still update frequency if that's what changed
            if (newFrequency !== null) {
              console.log('[handleSigsChange] API no result, updating frequency to:', newFrequency)
              return { ...prevValues, frequency: newFrequency }
            }
            return prevValues
          }
        })
        setLoading(false)
      }).catch(() => setLoading(false))
    } else {
      // Still update frequency if that's what changed, even without num_units
      if (e.target.name === 'frequency') {
        const newFreq = e.target.value
        console.log('[handleSigsChange] no payload, updating frequency to:', newFreq)
        setValues((prevValues: any) => ({
          ...prevValues,
          frequency: newFreq,
        }))
      } else {
        setValues((prevValues: any) => ({
          ...prevValues,
          nn_sig_directions: '',
        }))
      }
    }
  }

  const handleDosageUpdate = (e: any) => {
    const name = e.target.name
    if (name === 'food_qualifier') {
      let foodQualifier: string = e.target.value
      setFoodQualifier(foodQualifier)
      if (MEDICATION_TIMINGS) {
        setDosageInfo([...dosageInfo, { foodQualifier, MEDICATION_TIMINGS }])
        setFoodQualifier('')
        setMedicineTiming('')
      }
    }
    if (name === 'medication_timings') {
      let medicationTimings: string = e.target.value
      setMedicineTiming(medicationTimings)
      // if (FOOD_QUALIFIER) {
      // setDosageInfo([...dosageInfo, { medicationTimings, FOOD_QUALIFIER }])
      setDosageInfo([{ medicationTimings }])

      setFoodQualifier('')
      setMedicineTiming('')
      // }
    }
  }

  const [different] = useState(differently)
  const patientService = new PatientService()
  const [open, setOpen] = React.useState(false)
  const [medicine, setMedicine] = useState([] as any)
  const [loading, setLoading] = useState(false)

  const handleSigRating = (rating: boolean) => {
    const prev = sigRating
    const newRating = prev === rating ? null : rating
    setSigRating(newRating)
    if (newRating !== null && medicineInfo?.id && patientId) {
      patientService.submitSigFeedback(patientId, medicineInfo.id, { sig_rating: newRating })
        .then(() => toast.success(`Sig rated ${newRating ? 'good' : 'bad'}`, Helper.bottom_center()))
        .catch((err: any) => {
          toast.error(Helper.getErrorText(err), Helper.bottom_center())
          setSigRating(prev)
        })
    }
  }

  const debounce = (func: any, delay: any) => {
    let debounceTimer: any;
    return function () {
      const context = func
      const args = arguments
      clearTimeout(debounceTimer)
      debounceTimer
        = setTimeout(() => func.apply(context, args), delay)
    }
  }

  useEffect(() => {
    let active = true;
    if (active && query.length >= 2) {
      try {
        setLoading(true)
        patientService.getPrescriptionUnit(query).then((response: any) => {
          setMedicine(response.data)
          setLoading(false)
        })
      } catch (error) {
        console.error(error)
        setLoading(false)
      }
    }
    return () => {
      active = false;
    };
  }, [query]);

  const onBtnCancel = (mode: string) => {
    outer(input.id, mode)
  }

  const onBtnSubmit = (_mode: string, _typeOut: string) => {
    if (!medicineInfo) {
      if (!oneMedicine) {
        return
      }
    }

    medicineInfo
      ? outerSubmit(values, _mode, _typeOut, medicineInfo, dosageInfo, currentOption)
      : outerSubmit(values, oneMedicine, dosageInfo, currentOption, _typeOut, _mode)
    handleClose()
  }

  const checkForm = (name: string) => {
    let check = false
    let masterCheck = false
    Object.keys(values).forEach((key, _val) => {
      check = checkCondition(name == key, true, checkCondition(values[key], true, false))

      if (!check) {
        masterCheck = true
      }
    })
    setSaveDisable(masterCheck)
  }

  const notesBox = () => {
    return <Box mt={1} mb={1}>
      <TextField
        id="notes-basic"
        label="Take notes here"
        variant="outlined"
        size="small"
        inputProps={{ style: { fontSize: '0.7rem' } }}
        InputLabelProps={{ style: { fontSize: '0.7rem' } }}
        name="note"
        value={values.note}
        onChange={handleInputChange}
        multiline={true}
        rows="3"
        className={classes.fullWidth}
      />
    </Box>
  }

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmContent('')
  }

  const confirmStatus = (param: boolean) => {
    if (param) {
      setConfirmOpen(false)
      const actions = confirmContent.split('/')
      let action = actions[0]
      let sub_action = actions[1]
      if (action === sub_action) sub_action = ''
      setLoading(true)
      const medPrg = (typeof medicineInfo?.programs === 'object') ? medicineInfo?.programs[0] : ''

      patientService.putPatientActions(patientId, medicineInfo.id, medPrg, action, sub_action, medicineInfo.care_plan_id)
        .then((_response: any) => {
          setLoading(false)
          // toast.success('Request processed successfully.', Helper.bottom_center())
          updatePatientData({ isMeasurement: true, isTreatments: true, })
        }).catch((error) => {
          setLoading(false)
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        })
    }
    handleClose()
  }

  return (<>
    {loading && <LinearProgress />}
    <Grid container direction="column">
      <Box className={classes.medicationsBox}>
        <Card>
          <CardContent>
            <Grid
              container
              className={classes.medicationsBoxDisplay}
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
            >
              <Grid item>
                <Typography
                  className={classes.header2}
                  variant="body2"
                  style={{
                    lineHeight: '1',
                    color: '#080808',
                    marginBottom: different ? '-1rem' : '',
                  }}
                >
                  <strong>{heading}</strong>
                </Typography>
                <Stack spacing={0}
                  className={classes.checkBoxShadow}
                  direction="row"
                  alignItems="center"
                >
                  <Checkbox
                    onChange={handleInputChange}
                    checked={values.prescription_taken}
                    name="prescription_taken"
                    inputProps={{ 'aria-label': 'Taken as needed', }}
                    sx={{ padding: '0rem !important', transform: "scale(0.6)", }}
                  />
                  <Typography variant='subtitle2' color="textSecondary" className={classes.desc}>
                    Taken as needed
                  </Typography>
                </Stack >
                <Autocomplete
                  id="combo-box-demo"
                  open={open}
                  onOpen={() => {
                    setOpen(true)
                    setMedicine([])
                    setQuery('')
                  }}
                  onClose={() => {
                    setOpen(false)
                    setMedicine([])
                    setQuery('')
                  }}
                  options={medicine as any}
                  getOptionLabel={(option: any) => Helper.getRXnorm(option?.name, option?.rxcui)}
                  filterOptions={(x) => x}
                  sx={{
                    display: noDropDown,
                    padding: '2px 0px 0px 0px'
                  }}
                  className={classes.autoCompleteDropDowns}
                  loading={loading}
                  value={oneMedicine}
                  onChange={(_event, newValue) => {
                    setOneMedicine(newValue)
                    setValues({
                      ...values,
                      unit: newValue?.unit || '',
                      route: newValue?.route || ''
                    })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      onChange={debounce((e: any) => {
                        setQuery(e.target.value)
                        if (e.target.value === '') {
                          setMedicine([])
                        }
                      }, 1000)}
                      label="Medicine Selection"
                      InputLabelProps={{ style: { fontSize: '0.7rem' } }}
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: '0.7rem' },
                        endAdornment: (
                          <React.Fragment>
                            {loading ? (
                              <CircularProgress color="inherit" size={10} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                <TakingOptions
                  options={options}
                  isPrgMed={false}
                  onBtnChange={onBtnChange}
                  currentOption={currentOption}
                  classes={classes}
                />
                {notesBox()}
                {
                  checkCondition(write,
                    <UpdateMedicine
                      medicineInfo={medicineInfo}
                      dosageInfo={dosageInfo}
                      handleDosageUpdate={handleDosageUpdate}
                      values={values}
                      classes={classes}
                      uiResource={uiResource}
                      handleInputChange={handleInputChange}
                      handleSigsChange={handleSigsChange}
                      sigRating={sigRating}
                      onSigRating={handleSigRating}
                    />,
                    <ViewMedicine
                      uiResource={uiResource}
                      medicineInfo={medicineInfo}
                      dosageInfo={dosageInfo}
                      classes={classes}
                    />
                  )
                }
              </Grid >
            </Grid >
          </CardContent >
          {!medicineInfo?.isMedDialog &&
            <ButtonGroup
              className={classes.questionButtonStyle}
              size="small"
              variant="text"
              aria-label="Confirm"
            >
              <Button
                className={classes.questionButtonWidth}
                disabled={checkCondition(medicineInfo, false, !oneMedicine)}
                sx={{ borderTop: 'none !important' }}
                onClick={() => onBtnSubmit('view', 'confirm')}
              >
                <strong>{`Save & Confirm`}</strong>
              </Button>
              <Button
                disabled={checkCondition(medicineInfo, false, !oneMedicine)}
                className={(true) ? classes.questionButtonWidth : classes.questionButtonWidth1}
                sx={{ borderTop: 'none !important' }}
                onClick={() => onBtnSubmit('view', 'save')}
              >
                <strong>Save</strong>
              </Button>
              <Button
                className={(true) ? classes.questionButtonWidth : classes.questionButtonWidth1}
                onClick={different ? () => onBtnCancel('view') : handleClose}
              >
                <strong>Cancel</strong>
              </Button>
            </ButtonGroup>
          }
        </Card >
      </Box >
    </Grid >
    {confirmOpen &&
      <ConfirmationDialogComponent
        open={confirmOpen}
        content={`Are you sure for action : ${confirmContent}`}
        handleConfirmClose={handleConfirmClose}
        confirmStatus={confirmStatus}
      />
    }
  </>
  )
}

interface WriteProps {
  classes: any
  values: any
  isPrgMed?: any
  handleInputChange: any
  handleSigsChange?: any
  handleDosageUpdate: any
  dosageInfo: any
  uiResource: any
  medicineInfo: any
  sigRating?: boolean | null
  onSigRating?: (rating: boolean) => void
}

export const UpdateMedicine: React.FC<WriteProps> = ({
  classes,
  values,
  isPrgMed,
  handleInputChange,
  handleSigsChange,
  handleDosageUpdate,
  sigRating,
  onSigRating,
  uiResource,
  dosageInfo,
  medicineInfo,
}) => {
  return (
    <>
      <Grid container direction="row" alignItems="center">
        <Grid container direction="row" alignItems="center" justifyContent="space-between">
          <Grid item xs={3}>
            <Typography
              color="textSecondary"
              className={classes.desc}
              variant="subtitle2"
            >
              No. Taken
            </Typography>
            <TextField
              variant="standard"
              id="standard-basic"
              size="small"
              value={values.num_units}
              onChange={handleInputChange}
              name="num_units"
              className={classes.medicationTextField}
              onBlur={handleSigsChange}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography color="textSecondary" className={classes.desc} variant="subtitle2">
              Unit
            </Typography>
            <Typography className={`${classes.tooltip1} ${classes.desc}`} variant="body2">
              {checkCondition(values.unit, values.unit, '-')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography color="textSecondary" className={classes.desc} variant="subtitle2">
              Route
            </Typography>
            <Typography className={`${classes.tooltip1} ${classes.desc}`} variant="body2">
              {checkCondition(values.route, values.route, '-')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography color="textSecondary" className={classes.desc} variant="subtitle2">
              Frequency
            </Typography>
            <FormControl className={classes.fullWidth}>
              <Select
                displayEmpty
                variant="standard"
                className={classes.selectEmpty}
                inputProps={{ 'aria-label': 'Without label' }}
                value={uiResource.MEDICATION_DOSAGE_FREQUENCY[values.frequency] ? values.frequency : ''}
                name="frequency"
                MenuProps={{ autoFocus: false }}
                onChange={(e) => {
                  console.log('[FREQUENCY SELECT] onChange fired:', e.target.value)
                  handleSigsChange(e)
                }}
              >
                {Object.keys(uiResource.MEDICATION_DOSAGE_FREQUENCY).map(
                  (_key, _index) => (
                    <MenuItem className={classes.formControlItemsPadding} value={_key}>
                      {uiResource.MEDICATION_DOSAGE_FREQUENCY[_key]}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container xs={12} direction="row" spacing={2} style={{ paddingTop: '0.5rem' }} justifyContent={"space-between"} alignItems="center">
          <Grid item xs={3}>
            <Typography
              color="textSecondary"
              className={classes.desc}
              variant="subtitle2"
            >
              Dose time
            </Typography>
            <FormControl className={classes.formControlWidth150}>
              <Select
                displayEmpty
                variant="standard"
                className={classes.selectEmpty}
                inputProps={{ 'aria-label': 'Without label' }}
                value={dosageInfo?.[0]?.medicationTimings || ''}
                name="medication_timings"
                MenuProps={{ autoFocus: false }}
                onChange={handleDosageUpdate}
              >
                {Object.keys(uiResource?.MEDICATION_TIMINGS || []).map(
                  (_key, index) => (
                    <MenuItem key={`does-time${index}`} className={classes.formControlItemsPadding} value={_key}>
                      {uiResource.MEDICATION_TIMINGS[_key]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          {medicineInfo?.sig_directions &&
            <Grid item xs={7} alignItems={'right'}>
              <Typography color="textSecondary" className={classes.tooltip1} variant="subtitle2">
                Imported directions
              </Typography>
              <Typography className={`${classes.tooltip1} ${classes.desc}`} variant="body2" color="textSecondary">
                {medicineInfo?.sig_directions}
              </Typography>
            </Grid>
          }

          {medicineInfo?.sig_directions &&
            <Grid item xs={12} alignItems={'left'}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box>
                  <Typography color="textSecondary" className={classes.tooltip1} variant="subtitle2">
                    Converted directions
                  </Typography>
                  <TextField
                    variant="standard"
                    id="standard-basic"
                    size="small"
                    value={values.nn_sig_directions}
                    onChange={handleInputChange}
                    onBlur={handleSigsChange}
                    name="nn_sig_directions"
                    sx={{ width: '11rem' }}
                  />
                </Box>
                {onSigRating && !Helper.isAIDA() && (
                  <Box display="flex" alignItems="center" gap={0.25} mt={2}>
                    <Tooltip title="AI parsed correctly">
                      <IconButton size="small" onClick={() => onSigRating(true)}
                        color={sigRating === true ? 'success' : 'default'}>
                        {sigRating === true ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="AI parsed incorrectly">
                      <IconButton size="small" onClick={() => onSigRating(false)}
                        color={sigRating === false ? 'error' : 'default'}>
                        {sigRating === false ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Grid>
          }
          {isPrgMed &&
            <Grid item xs={2.7}>
              <Typography color="textSecondary" className={classes.desc} variant="subtitle2">
                Disallow substitution
              </Typography>
              <FormControl className={classes.fullWidth}>
                <Select
                  displayEmpty
                  variant="standard"
                  className={classes.selectEmpty}
                  inputProps={{ 'aria-label': 'disallow_substitution' }}
                  value={values.disallow_substitution}
                  name="disallow_substitution"
                  MenuProps={{ autoFocus: false }}
                  onChange={handleInputChange}
                >
                  <MenuItem className={classes.formControlItemsPadding} value={true as any}>Yes</MenuItem>
                  <MenuItem className={classes.formControlItemsPadding} value={false as any}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          }
          <Grid item xs={3}>
            <Typography color="textSecondary" className={classes.desc} variant="subtitle2">
              Current dose started
            </Typography>
            <TextField
              variant="standard"
              size="small"
              type="date"
              value={values.current_dose_start_date
                ? new Date(values.current_dose_start_date * 1000).toISOString().split('T')[0]
                : ''}
              onChange={handleInputChange}
              name="current_dose_start_date"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split('T')[0] }}
              sx={{ width: '100%' }}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

interface ReadProps {
  medicineInfo: any
  uiResource: any
  dosageInfo: any
  classes: any
}

export const ViewMedicine: React.FC<ReadProps> = ({
  medicineInfo,
  uiResource,
  dosageInfo,
  classes,
}) => {
  return (
    <>
      <Grid container xs={12} direction="row" alignItems="center">
        <Grid container xs={12} direction="row" alignItems="center" justifyContent="space-between">
          <Grid item xs={3}>
            <Typography
              color="textSecondary"
              className={classes.tooltip1}
              variant="subtitle2"
            >
              No. Taken
            </Typography>
            <Typography
              color="textSecondary"
              className={classes.tooltip1}
              variant="subtitle2"
            >
              <strong>{checkCondition(medicineInfo.pu_order?.num_units, medicineInfo.pu_order?.num_units, '-')}</strong>
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              color="textSecondary"
              className={classes.tooltip1}
              variant="subtitle2"
            >
              Unit
            </Typography>
            <Typography className={`${classes.tooltip1} ${classes.desc}`} variant="body2">
              {medicineInfo.pu_order?.unit}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography color="textSecondary" className={classes.tooltip1} variant="subtitle2">
              Route
            </Typography>
            <Typography className={`${classes.tooltip1} ${classes.desc}`} variant="body2">
              {medicineInfo?.pu_order?.route || ''}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              color="textSecondary"
              className={classes.tooltip1}
              variant="subtitle2"
            >
              Frequency
            </Typography>
            <Typography
              color="textSecondary"
              className={classes.tooltip1}
              variant="subtitle2"
            >
              <strong>{uiResource.MEDICATION_DOSAGE_FREQUENCY[medicineInfo.pu_order?.frequency] || '-'}</strong>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography color="textSecondary" className={classes.tooltip1} variant="subtitle2">
              Dose time
            </Typography>
            <Grid container direction="row" alignItems="center">
              {dosageInfo?.map((item: any, index: any) => (
                <Grid xs={4} key={`view-does-time-${index}`}>
                  <FormControl className={classes.formControlWidth150}>
                    <Select
                      disabled
                      variant="standard"
                      inputProps={{ 'aria-label': 'medication_timings_view' }}
                      value={item?.medicationTimings || ''}
                      name="medication_timings_view"
                      MenuProps={{ autoFocus: false }}
                    >
                      {Object.keys(uiResource?.MEDICATION_TIMINGS || []).map(
                        (_key, index) => (
                          <MenuItem key={`does-time-view${index}`} className={classes.formControlItemsPadding} value={_key}>
                            {uiResource.MEDICATION_TIMINGS[_key]}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

interface OptionProps {
  options: any
  isPrgMed: any
  onBtnChange: any
  currentOption: any
  classes: ClassNameMap
}

export const TakingOptions: React.FC<OptionProps> = ({
  options,
  isPrgMed,
  onBtnChange,
  currentOption,
  classes,
}) => {

  return (
    <Box pt={2} pb={1} className={isPrgMed ? classes.questionHeaderOtherMedSection : classes.questionHeaderSection}>
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1.2}
      >
        {options.map((r: any, index: any) => (
          <Grid item sx={{ paddingTop: '0.3rem !important' }}>
            <Button
              variant="outlined"
              key={`taking-btn-${index}`}
              name={r}
              size="small"
              onClick={(_e) => onBtnChange(r)}
              className={
                currentOption === r
                  ? [
                    classes.questionSubmitAnsButton,
                    classes.questionSubmitAnsButtonActive,
                  ].join(' ')
                  : classes.questionSubmitAnsButton
              }
            >
              <strong>  {Helper.toCamelCase(r)}     </strong>
            </Button>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default withStyles(stylesheet)(MedicationsComponent)
